import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const RefundPolicyPage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_15px_rgba(34,197,94,0.5)]"></div>
          <span className="font-orbitron text-2xl tracking-wider text-green-400 uppercase">
            ［ Refund Policy ］
          </span>
        </div>
        <p className="text-sm text-gray-400 font-rajdhani">
          Effective Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Content */}
      <div className="system-window rounded-lg p-6 max-w-4xl">
        <div className="prose prose-invert max-w-none">
          <div className="space-y-6 text-gray-300 font-rajdhani">
            {/* Important Notice */}
            <div className="bg-orange-950/20 border-2 border-orange-500/50 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚠️</span>
                <div>
                  <p className="font-orbitron font-bold text-orange-400 text-sm mb-1">
                    IMPORTANT NOTICE
                  </p>
                  <p className="text-orange-200 text-xs font-rajdhani">
                    AwakenArc offers a 14-day refund window for new subscriptions, with additional refunds available for billing errors, duplicate charges, or where required by law. Please review this policy carefully before subscribing.
                  </p>
                </div>
              </div>
            </div>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">1. Overview</h2>
              <p className="leading-relaxed mb-3">
                This Refund Policy explains the circumstances under which refunds may be issued for AwakenArc subscription services. By subscribing to any of our paid plans, you acknowledge and agree to this policy.
              </p>
              <p className="leading-relaxed">
                AwakenArc is a digital service providing instant access to features upon subscription. We offer a 14-day refund window for new subscriptions, with additional refunds available in specific circumstances outlined below.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">2. Free Trial Period</h2>
              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">Before You Subscribe:</strong> We strongly encourage all new users to take advantage of our free trial to evaluate the Service before making a financial commitment.
              </p>
              
              <div className="bg-system-darker border border-green-500/30 rounded-lg p-4 mb-3">
                <p className="font-orbitron font-bold text-white mb-2">🎁 14-Day Free Trial</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                  <li>Full access to S-Rank features for 14 days</li>
                  <li>No credit card required</li>
                  <li>No automatic charges during trial</li>
                  <li>Cancel anytime during trial with no obligation</li>
                  <li>Test all features before committing to a subscription</li>
                </ul>
              </div>

              <p className="leading-relaxed">
                The trial period is designed to give you ample time to evaluate whether AwakenArc meets your needs. By subscribing after the trial, you acknowledge that you have tested the Service and found it satisfactory.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">3. Refund Policy</h2>
              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">14-Day Refund Window:</strong> In compliance with our payment processor's requirements, we offer a 14-day refund period for new subscriptions. However, due to the nature of our digital service, this policy has specific conditions.
              </p>

              <div className="bg-blue-950/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-2">🕒 14-Day Refund Window</p>
                <p className="text-sm mb-2">
                  <strong className="text-blue-400">New Subscriptions Only:</strong> Refunds are available within 14 days of your initial subscription purchase, provided you have not extensively used the Service.
                </p>
                <p className="text-xs text-blue-200">
                  <strong>Important:</strong> Extensive usage (creating multiple quests, using AI features extensively, or heavy engagement) may disqualify you from a refund, as you've received significant value from the Service.
                </p>
              </div>

              <p className="leading-relaxed mb-3">
                <strong>No refunds will be issued for:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Refund requests made after 14 days from initial subscription</li>
                <li>Extensive use of the Service during the refund period</li>
                <li>Renewal subscriptions (only initial subscriptions qualify)</li>
                <li>Partial subscription periods after 14 days</li>
                <li>Subscription cancellations (you retain access until period ends)</li>
                <li>Technical difficulties on your end (device, internet connection, etc.)</li>
                <li>User error or misunderstanding of features</li>
                <li>Account termination due to Terms of Service violations</li>
                <li>Failure to use the Service during your subscription period</li>
              </ul>

              <p className="leading-relaxed">
                <strong>Why We Have This Policy:</strong> While we offer a 14-day refund window for new subscriptions, extensive usage indicates you've received significant value from our Service. This policy balances customer protection with fair business practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">4. Additional Refund Circumstances</h2>
              <p className="leading-relaxed mb-3">
                In addition to the 14-day refund window for new subscriptions, refunds <strong>will also be</strong> issued in the following circumstances:
              </p>

              <div className="space-y-3 mb-4">
                <div className="bg-system-darker border-l-4 border-green-500 p-4 rounded">
                  <p className="font-bold text-green-400 mb-2">✅ Billing Errors</p>
                  <p className="text-sm mb-2">If you were charged incorrectly due to a system error, pricing error, or technical malfunction, we will issue a full refund of the incorrect charge.</p>
                  <p className="text-xs text-gray-500">Example: You were charged $19.99 instead of the advertised $9.99</p>
                </div>

                <div className="bg-system-darker border-l-4 border-green-500 p-4 rounded">
                  <p className="font-bold text-green-400 mb-2">✅ Duplicate Charges</p>
                  <p className="text-sm mb-2">If your payment method was charged multiple times for the same subscription period, we will immediately refund the duplicate charge(s).</p>
                  <p className="text-xs text-gray-500">Example: You were charged twice in the same billing cycle</p>
                </div>

                <div className="bg-system-darker border-l-4 border-green-500 p-4 rounded">
                  <p className="font-bold text-green-400 mb-2">✅ Unauthorized Charges</p>
                  <p className="text-sm mb-2">If you can demonstrate that a charge was made without your authorization (e.g., account was hacked), we will investigate and may issue a refund after verification.</p>
                  <p className="text-xs text-gray-500">Note: You are responsible for securing your account. Refunds may not be issued if unauthorized access resulted from your negligence.</p>
                </div>

                <div className="bg-system-darker border-l-4 border-green-500 p-4 rounded">
                  <p className="font-bold text-green-400 mb-2">✅ Service Unavailability (Extended Outage)</p>
                  <p className="text-sm mb-2">If the Service is completely unavailable for more than 7 consecutive days due to issues on our end (not including scheduled maintenance), you may be eligible for a prorated refund.</p>
                  <p className="text-xs text-gray-500">Note: Brief outages, scheduled maintenance, or issues with your internet connection do not qualify.</p>
                </div>

                <div className="bg-system-darker border-l-4 border-green-500 p-4 rounded">
                  <p className="font-bold text-green-400 mb-2">✅ Legal Requirements</p>
                  <p className="text-sm">Where refunds are required by applicable law in your jurisdiction (such as EU consumer protection laws), we will comply with those legal requirements.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">5. EU Consumer Rights (14-Day Right of Withdrawal)</h2>
              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">For Users in the European Union:</strong>
              </p>
              <p className="leading-relaxed mb-3">
                Under EU consumer protection law (Directive 2011/83/EU), consumers have a 14-day right of withdrawal for online purchases. However, this right does NOT apply to digital content if:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>The digital content is not supplied on a tangible medium (it's cloud-based)</li>
                <li>Performance has begun with your prior express consent</li>
                <li>You acknowledged that you lose your right of withdrawal once performance begins</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong>By subscribing to AwakenArc, you:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Give your express consent for immediate access to digital content</li>
                <li>Acknowledge that you lose your right of withdrawal upon accessing the Service</li>
                <li>Understand that the Service is delivered immediately upon subscription</li>
              </ul>

              <div className="bg-blue-950/20 border border-blue-500/30 rounded-lg p-4 mb-3">
                <p className="text-sm text-blue-400 font-rajdhani">
                  <strong>Important for EU Users:</strong> Your 14-day free trial period serves as an opportunity to evaluate the Service before making a purchase. Once you subscribe and access the Service, your withdrawal right ends as permitted under EU law.
                </p>
              </div>

              <p className="leading-relaxed">
                Despite this, if you believe you are entitled to a refund under EU consumer protection laws and the circumstances in Section 4 do not apply, please contact us at umarkhodzha@awakenarc.com. We will review each case individually.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">6. Subscription Cancellation</h2>
              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">Cancellation vs. Refund:</strong> It's important to understand the difference between canceling your subscription and requesting a refund.
              </p>

              <div className="bg-system-darker border border-green-500/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-3">When You Cancel Your Subscription:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                  <li>You retain full access until the end of your current billing period</li>
                  <li>You will not be charged for subsequent billing periods</li>
                  <li>No refund is automatically issued for the current period</li>
                  <li>You can reactivate your subscription at any time</li>
                  <li>Your data is preserved (not deleted) unless you delete your account</li>
                </ul>
              </div>

              <p className="leading-relaxed mb-3">
                <strong>How to Cancel:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Log into your account</li>
                <li>Go to Settings → Account → Subscription Management</li>
                <li>Click "Cancel Subscription"</li>
                <li>Or manage through your payment processor's customer portal</li>
              </ul>

              <p className="leading-relaxed">
                Canceling your subscription does NOT entitle you to a refund for the current billing period. If you believe you qualify for a refund under Section 4, you must submit a separate refund request.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">7. Subscription Tier Changes</h2>
              
              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">Upgrades:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Take effect immediately</li>
                <li>You are charged the prorated difference</li>
                <li>New features available right away</li>
                <li>No refund if you later downgrade or cancel</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">Downgrades:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Take effect at the start of your next billing cycle</li>
                <li>You keep higher-tier access until period ends</li>
                <li>No prorated credit or refund issued</li>
                <li>Lower price applies to next billing cycle</li>
              </ul>

              <p className="leading-relaxed">
                No refunds are issued for subscription tier changes in either direction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">8. How to Request a Refund</h2>
              <p className="leading-relaxed mb-3">
                If you believe you qualify for a refund under the 14-day window (Section 3) or the additional circumstances listed in Section 4, please follow this process:
              </p>

              <div className="bg-system-darker border border-green-500/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-3">Refund Request Process:</p>
                <ol className="list-decimal list-inside space-y-2 ml-4 text-sm">
                  <li><strong>Contact Us:</strong> Email umarkhodzha@awakenarc.com or submit a request through www.awakenarc.com/support</li>
                  <li><strong>Subject Line:</strong> Use "Refund Request" in the subject line</li>
                  <li><strong>Provide Information:</strong>
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Your account email address</li>
                      <li>Transaction date and amount</li>
                      <li>Reason for refund request</li>
                      <li>Supporting evidence (screenshots, error messages, etc.)</li>
                    </ul>
                  </li>
                  <li><strong>Review Period:</strong> We will review your request within 3-5 business days</li>
                  <li><strong>Decision:</strong> You will receive a decision via email</li>
                  <li><strong>Processing:</strong> If approved, refunds are processed within 5-10 business days to your original payment method</li>
                </ol>
              </div>

              <p className="leading-relaxed mb-3">
                <strong>Required Documentation:</strong> Depending on the reason for your refund request, we may ask for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Screenshots of error messages</li>
                <li>Bank statements showing duplicate charges</li>
                <li>Evidence of technical issues</li>
                <li>Police report (for unauthorized access claims)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">9. Chargebacks and Disputes</h2>
              
              <div className="bg-red-950/20 border-2 border-red-500/50 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-red-400 mb-2">⚠️ IMPORTANT - Please Read Before Filing a Chargeback</p>
                <p className="text-sm text-red-200">
                  We strongly urge you to contact us directly before initiating a chargeback with your bank or payment provider. Chargebacks have serious consequences for both parties.
                </p>
              </div>

              <p className="leading-relaxed mb-3">
                <strong className="text-green-400">Consequences of Chargebacks:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Immediate Account Suspension:</strong> Your account will be immediately suspended</li>
                <li><strong>Data Loss:</strong> All account data may be deleted</li>
                <li><strong>No Service Access:</strong> You will lose access to all features</li>
                <li><strong>Permanent Ban:</strong> You may be permanently banned from creating new accounts</li>
                <li><strong>Additional Fees:</strong> We may seek compensation for chargeback fees ($15-$100) plus any damages</li>
                <li><strong>Legal Action:</strong> We reserve the right to pursue legal action for fraudulent chargebacks</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong>When Chargebacks Are Appropriate:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>You don't recognize the charge (possible fraud)</li>
                <li>We refuse a legitimate refund request</li>
                <li>We are unresponsive to your refund request for more than 30 days</li>
              </ul>

              <p className="leading-relaxed">
                <strong>Our Commitment:</strong> We respond to all legitimate refund requests within 3-5 business days. Give us a chance to resolve the issue before escalating to a chargeback. In most cases, we can resolve disputes faster and more fairly than the chargeback process.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">10. Payment Processor</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc processes payments through Paddle, our third-party payment processor. All payment transactions are handled securely by Paddle.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>We do not store or have access to your credit card details</li>
                <li>All payment data is encrypted and PCI-DSS compliant</li>
                <li>For payment-specific issues, you may also contact Paddle directly</li>
                <li>Refunds are processed through the same payment method used for purchase</li>
                <li>Paddle's Buyer Terms include a 14-day minimum refund policy, which we comply with</li>
              </ul>
              <p className="leading-relaxed">
                <strong>Note:</strong> This refund policy complies with Paddle's requirements while maintaining our service-specific terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">11. Subscription Promotional Pricing</h2>
              <p className="leading-relaxed mb-3">
                Our current subscription prices include a 50% promotional discount:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>E-Rank Hunter:</strong> $2.99/month (regularly $5.99)</li>
                <li><strong>S-Rank Hunter:</strong> $4.99/month (regularly $9.99)</li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>Important Notes:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Promotional pricing is subject to change with 30 days' notice</li>
                <li>If you subscribe at promotional pricing, that rate is locked in for as long as you maintain an active subscription</li>
                <li>No refunds will be issued if promotional pricing ends or changes</li>
                <li>The 14-day free trial allows you to evaluate the Service before subscribing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">12. Changes to This Refund Policy</h2>
              <p className="leading-relaxed mb-3">
                We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting. When we make changes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>We will update the "Effective Date" at the top of this page</li>
                <li>Material changes will be announced via email and in-app notification</li>
                <li>Continued use of the Service after changes constitutes acceptance</li>
                <li>Changes apply to all future subscriptions and renewals</li>
              </ul>
              <p className="leading-relaxed">
                We encourage you to review this policy periodically, especially before subscribing or renewing.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-green-400 mb-3">13. Contact Information</h2>
              <p className="leading-relaxed mb-3">
                For refund requests, billing questions, or concerns about this policy, please contact us:
              </p>
              <div className="bg-system-darker border border-green-500/30 rounded-lg p-4">
                <p className="leading-relaxed mb-2">
                  <strong className="text-green-400">Email:</strong> <a href="mailto:umarkhodzha@awakenarc.com" className="text-green-400 hover:underline">umarkhodzha@awakenarc.com</a>
                </p>
                <p className="leading-relaxed mb-2">
                  <strong className="text-green-400">Support:</strong> <a href="https://www.awakenarc.com/support" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">www.awakenarc.com/support</a>
                </p>
                <p className="leading-relaxed mb-2">
                  <strong className="text-green-400">Subject Line for Refunds:</strong> "Refund Request"
                </p>
                <p className="leading-relaxed">
                  <strong className="text-green-400">Response Time:</strong> 3-5 business days for refund requests, 24-48 hours for general inquiries
                </p>
              </div>
            </section>

            <section className="mt-8 pt-8 border-t border-gray-700">
              <div className="bg-system-darker border-l-4 border-green-500 p-4 rounded">
                <p className="font-orbitron font-bold text-white mb-2">💡 Important Reminders</p>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li><strong>Use the Free Trial:</strong> Take full advantage of the 14-day free trial to test all features before subscribing</li>
                  <li><strong>14-Day Refund Window:</strong> New subscriptions have a 14-day refund period if you haven't extensively used the Service</li>
                  <li><strong>Contact Us First:</strong> If you have any issues, contact support before requesting a refund or chargeback</li>
                  <li><strong>Read Terms of Service:</strong> Familiarize yourself with our Terms of Service to understand what you're subscribing to</li>
                  <li><strong>Cancel Anytime:</strong> You can cancel your subscription at any time, though no refund is issued for the current period</li>
                </ul>
              </div>
            </section>

            <section className="mt-6">
              <p className="text-sm text-gray-500 leading-relaxed">
                Thank you for understanding our refund policy. We strive to provide excellent service and support to all our users. If you have any questions or concerns, we're here to help! ⚔️
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
